import { Layout } from 'antd';
import { useContext, useState } from 'react';
import { useParams } from 'react-router-dom';

import { CurrencyWrapper } from 'app/components/commons/Currency/CurrencyWrapper/CurrencyWrapper';
import { Mode } from 'app/components/commons/Hotel/Types';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import type { BookingVoucherResponse } from 'app/typings/core/admin/bookings/booking';

import styles from './BookingDetails.module.scss';
import { BookingDetailsHeader } from './BookingDetailsHeader';
import { DetailsContent } from './_components/DetailsContent';
import { useBookingDetails } from './_hooks/useBookingDetails';
import { BookingModals } from './_modals/Modals';

const BookingDetailsInner = () => {
  const { enterEditMode } = useContext(ViewEditModeContext);
  const { bookingCode } = useParams<{ bookingCode: string }>();

  const [editReviewId, setEditReviewId] = useState<number | undefined>(
    undefined
  );
  const [selectedVoucher, setSelectedVoucher] = useState<
    BookingVoucherResponse | undefined
  >(undefined);

  const {
    bookingDetails,
    reloadBooking,
    resendUserConfirmationMail,
    resendHotelConfirmationMail,
    refundModalVisible,
    setRefundModalVisible,
    isVoucherModalVisible,
    setIsVoucherModalVisible,
    isEditEmailModalVisible,
    setIsEditEmailModalVisible,
    isCancelModalVisible,
    setIsCancelModalVisible,
    feedbackInModal,
    setFeedbackInModal,
    isVccDebited,
    isEditReviewModalVisible,
    setIsEditReviewModalVisible,
  } = useBookingDetails(bookingCode);

  const isBookingValid =
    bookingDetails !== null &&
    bookingDetails !== undefined &&
    bookingDetails.booking.code === bookingCode;

  return (
    <Layout className={styles.bookingDetails}>
      <BookingDetailsHeader
        title={`Booking ${bookingCode}`}
        mode={Mode.View}
        isLoading={false}
        onEditEmail={() => {
          setIsEditEmailModalVisible(true);
          enterEditMode();
        }}
      >
        {isBookingValid && (
          <CurrencyWrapper countryId={bookingDetails.booking.hotel.countryId}>
            <DetailsContent
              bookingDetails={bookingDetails.booking}
              bookingCode={bookingCode}
              reloadBooking={reloadBooking}
              resendUserConfirmationMail={resendUserConfirmationMail}
              resendHotelConfirmationMail={resendHotelConfirmationMail}
              onOpenRefundModal={() => setRefundModalVisible(true)}
              onOpenVoucherModal={(voucher?: BookingVoucherResponse) => {
                setSelectedVoucher(voucher);
                setIsVoucherModalVisible(true);
                enterEditMode();
              }}
              onEditFeedback={setFeedbackInModal}
              onCancelBooking={() => {
                setIsCancelModalVisible(true);
                enterEditMode();
              }}
              onEditReview={(reviewId) => {
                setIsEditReviewModalVisible(true);
                setEditReviewId(reviewId);
                enterEditMode();
              }}
            />

            <BookingModals
              bookingDetails={bookingDetails.booking}
              bookingCode={bookingCode}
              reloadBooking={reloadBooking}
              isVccDebited={isVccDebited}
              refundModalVisible={refundModalVisible}
              onCloseRefundModal={() => setRefundModalVisible(false)}
              selectedVoucher={selectedVoucher}
              isVoucherModalVisible={isVoucherModalVisible}
              onCloseVoucherModal={() => {
                setIsVoucherModalVisible(false);
                setSelectedVoucher(undefined);
              }}
              isEditEmailModalVisible={isEditEmailModalVisible}
              setIsEditEmailModalVisible={setIsEditEmailModalVisible}
              isCancelModalVisible={isCancelModalVisible}
              setIsCancelModalVisible={setIsCancelModalVisible}
              feedbackInModal={feedbackInModal}
              onCloseFeedbackModal={() => setFeedbackInModal(undefined)}
              isEditReviewModalVisible={isEditReviewModalVisible}
              setIsEditReviewModalVisible={setIsEditReviewModalVisible}
              editReviewId={editReviewId}
            />
          </CurrencyWrapper>
        )}
      </BookingDetailsHeader>
    </Layout>
  );
};

export const BookingDetails = () => (
  <ViewEditModeContextProvider>
    <BookingDetailsInner />
  </ViewEditModeContextProvider>
);
